import { CommonModule } from "@angular/common";
import { Component, model, OnInit, signal } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import {
  FUNCTION_RESULT_STATUS,
  FunctionResult,
  IObjectBase,
} from "@nova/common-interfaces";
import {
  IAsset,
  ICustomerOverview,
  IInvoice,
  IPayments,
  ISubscription,
} from "@rfm/shared-interfaces";
import { BlockUIModule } from "ng-block-ui";
import { ConfirmationService, MenuItem, MessageService } from "primeng/api";
import { BreadcrumbModule } from "primeng/breadcrumb";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { DialogService } from "primeng/dynamicdialog";
import { MenuModule } from "primeng/menu";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { ToastModule } from "primeng/toast";
import { ToggleSwitchModule } from "primeng/toggleswitch";
import { CommonComponent } from "../../../../../lib/components/index.mjs";
import { BoolToTextPipe } from "../../../../../lib/pipes/bool-to-text.pipe.mjs";
import { JsonToTextPipe } from "../../../../../lib/pipes/json-to-text.pipe.mjs";
import { LatestInvoicePaymentComponent } from "../../../../billing/components/make-payment/latest-invoice-payment/latest-invoice-payment.component";
import { PaymentMethodsComponent } from "../../../../shared/components/financial/payment-methods/payment-methods.component";
import { AssetService } from "../../../../shared/services/assets/asset.service";
import { CustomerService } from "../../../../shared/services/customers/customers.service.mjs";
import { InvoiceService } from "../../../../shared/services/financial/invoice.service.mjs";
import { PaymentService } from "../../../../shared/services/financial/payment.service.mjs";
import { SubscriptionService } from "../../../../shared/services/products/subscription.service.mjs";
import { AssetCardComponent } from "../../assets/asset-card/asset-card.component";
import { InvoiceCardComponents } from "../../financial/invoices/invoice-card/invoice-card.component";
import { PaymentCardComponents } from "../../financial/payments/payment-card/payment-card.component";
import { SubscriptionCardComponent } from "../../subscriptions/subscription-card/subscription-card.component";

@Component({
  selector: "app-customer-view",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SubscriptionCardComponent,
    ConfirmDialogModule,
    ToastModule,
    AssetCardComponent,
    BreadcrumbModule,
    InvoiceCardComponents,
    PaymentCardComponents,
    BlockUIModule,
    CardModule,
    JsonToTextPipe,
    ProgressSpinnerModule,
    MenuModule,
    ButtonModule,
    PaymentMethodsComponent,
    ToggleSwitchModule,
    LatestInvoicePaymentComponent,
    BoolToTextPipe,
  ],
  providers: [MessageService, ConfirmationService, DialogService],
  templateUrl: "./customer-view.component.html",
  styleUrl: "./customer-view.component.scss",
})
export class CustomerViewComponent extends CommonComponent implements OnInit {
  onPaymentProcessed() {}

  customer_id = model<string>();

  has_default_payment_method = model<boolean>(false);

  customer = signal<ICustomerOverview | undefined>(undefined);

  subscriptions: ISubscription[] | undefined;
  assets: IAsset[] | undefined;
  invoices: IInvoice[] | undefined;
  payments: IPayments[] | undefined;
  loading = true;

  breadCrumbItems: MenuItem[] | undefined;

  home: MenuItem | undefined;

  items: MenuItem[] = [
    {
      label: "Edit",
      icon: "pi pi-file-edit",
      command: () => this.edit(),
    },
  ];

  getInvoiceData(id: string): Promise<void | FunctionResult<IInvoice[]>> {
    return this.invoiceService.getByCustomer(id).then((result) => {
      if (result.status === FUNCTION_RESULT_STATUS.SUCCESS) {
        this.invoices = sortObjectArrayDescending(
          result.data!,
          "createdAt"
        ) as IInvoice[];
      }
      return Promise.resolve();
    });
  }

  edit() {
    this.router.navigate(["/admin/customers/edit", this.customer()?.id]);
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    protected customerService: CustomerService,
    private subscriptionService: SubscriptionService,
    private assetService: AssetService,
    protected invoiceService: InvoiceService,
    protected paymentService: PaymentService
  ) {
    super();
    this.isLoading(true);

    this.isLoading$.subscribe((state) => {
      this.loading = state;
    });
  }

  async refreshInvoices(state: boolean) {
    return this.getInvoiceData(this.customer()?.id!);
  }

  ngOnInit(): void {
    this.breadCrumbItems = [
      {
        label: "Customers",
        command: () => this.router.navigate(["/admin/customers"]),
      },
    ];

    this.home = { icon: "pi pi-home", routerLink: "/" };

    this.activatedRoute.params.subscribe(async (params) => {
      this.loading = true;

      let id = params["id"];
      console.log("Firing OnInit", id);

      this.customer_id.set(id);

      this.loadData();
    });
  }

  loadData() {
    const promises: Promise<any>[] = [];

    const customerPromise = this.customerService
      .getOverview(this.customer_id()!)
      .then((result) => {
        if (result.status == FUNCTION_RESULT_STATUS.SUCCESS) {
          this.customer.set(result.data!);
        }
        return Promise.resolve();
      });

    promises.push(customerPromise);

    const subscriptionPromise = this.subscriptionService
      .getByCustomer(this.customer_id()!)
      .then((result) => {
        if (result.status === FUNCTION_RESULT_STATUS.SUCCESS) {
          this.subscriptions = result.data;
        }
        return Promise.resolve();
      });

    promises.push(subscriptionPromise);

    const assetPromise = this.assetService
      .getByCustomer(this.customer_id()!)
      .then((result) => {
        if (result.status === FUNCTION_RESULT_STATUS.SUCCESS) {
          this.assets = result.data;
        }
        return Promise.resolve();
      });

    promises.push(assetPromise);

    const invoicePromise = this.getInvoiceData(this.customer_id()!);

    promises.push(invoicePromise);

    const paymentPromise = this.paymentService
      .getByCustomer(this.customer_id()!)
      .then((result) => {
        if (result.status === FUNCTION_RESULT_STATUS.SUCCESS) {
          this.payments = sortObjectArrayDescending(
            result.data!,
            "createdAt"
          ) as IPayments[];
        }
        return Promise.resolve();
      });

    promises.push(paymentPromise);

    Promise.all(promises).finally(() => {
      this.breadCrumbItems?.push({
        label: this.customer()?.name ?? "Customer",
        command: () =>
          this.router.navigate(["/admin/customers/view", this.customer()?.id]),
      });

      this.loading = false;
      this.isLoading(false);
    });
  }
}

function sortObjectArrayDescending(
  arr: IObjectBase[],
  property: keyof IObjectBase
): IObjectBase[] {
  const sortedArr = [...arr];
  sortedArr.sort((a, b) => {
    if (a[property]! < b[property]!) return 1;
    if (a[property]! > b[property]!) return -1;
    return 0;
  });
  return sortedArr;
}
